import type { CSSInterpolation } from '@emotion/serialize';
import type { Dispatch, Key, ReactNode } from 'react';

import {
	HubTooltip,
	Menu,
	MenuItem,
	MenuList,
} from '@adflow/frontend/ui-common';
import { typography } from '@adflow/frontend/ui-theme';

import { DataTableSelectorButton } from './DataTableSelectorButton';
import { useDataTableMeta } from './useDataTableMeta';

interface SelectionMeta<T> {
	selected: (string | number)[];
	setSelected: Dispatch<React.SetStateAction<(string | number)[]>>;
	items: T[];
}

export interface SelectOption<T> {
	isVisible: boolean;
	onSelect: ({ selected, setSelected, items }: SelectionMeta<T>) => void;
	content: ReactNode;
	key: Key;

	disabled?: boolean;
	tooltipTitle?: ReactNode;
	testid?: string;
	isLink?: boolean;

	style?: CSSInterpolation;
}

interface DataTableSelectorMenuProps<T> {
	items: T[];
	options: SelectOption<T>[];
	getId: (item: T) => number | string;
	isDisabled?: (item: T) => boolean;
}

export function DataTableSelectorMenu<T>({
	items,
	options,
	getId,
	isDisabled,
}: DataTableSelectorMenuProps<T>) {
	const { selected, setSelected } = useDataTableMeta();

	return (
		<div
			css={{
				margin: 'auto',
				marginLeft: 0,
				height: 'auto',
			}}
		>
			<Menu
				trigger={
					<DataTableSelectorButton
						items={items}
						getId={getId}
						isDisabled={isDisabled}
					/>
				}
			>
				<MenuList data-testid="datatable-selector-popover">
					{options.map((option) =>
						option.isVisible ? (
							<HubTooltip
								key={option.key}
								title={option.tooltipTitle}
								placement="right"
								PopperProps={{
									modifiers: [
										{
											name: 'offset',
											options: {
												offset: [0, -10],
											},
										},
									],
								}}
							>
								<MenuItem
									onSelect={() => {
										if (!option.disabled) {
											option.onSelect({ selected, setSelected, items });
										}
									}}
									css={[
										option.isLink && typography.clickableLink,
										option.style,
									]}
									data-testid={option.testid}
									disabled={option.disabled}
								>
									{option.content}
								</MenuItem>
							</HubTooltip>
						) : null
					)}
				</MenuList>
			</Menu>
		</div>
	);
}
